import { Component, OnInit, signal, inject, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { TournamentService } from '../../services/tournament/tournament.service';
import { LoggingService } from '../../services/shared/logging/logging.service';
import { ToastrService } from 'ngx-toastr';
import { DialogService } from '../../services/shared/dialog/dialog.service';
import { firstValueFrom, forkJoin } from 'rxjs';
import { ScorecardService } from '../../services/scorecard/scorecard.service';

@Component({
  selector: 'app-round-close',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './round-close.component.html',
  styleUrls: ['./round-close.component.scss']
})
export class RoundCloseComponent implements OnInit {
  private readonly tournamentService = inject(TournamentService);
  private readonly loggingService = inject(LoggingService);
  private readonly toastr = inject(ToastrService);
  private readonly router = inject(Router);
  private readonly dialogService = inject(DialogService);
  private readonly scorecardService = inject(ScorecardService);

  // Signals
  selectedTournament = this.tournamentService.selectedTournamentSignal;
  selectedRound = this.tournamentService.selectedRoundSignal;
  rounds = signal<any[]>([]);
  isLoading = signal(false);
  isClosing = signal(false);
  isMarkingOfficial = signal(false);
  closeResult = signal<any>(null);
  unofficialScorecards = signal<any[]>([]);
  unofficialCount = signal(0);

  // Computed
  canClose = computed(() => {
    const round = this.selectedRound();
    return round && round.active && !round.official;
  });

  showCloseButton = computed(() => this.canClose() && !this.closeResult() && this.unofficialCount() === 0 && !this.isMarkingOfficial());

  hasUnofficialScorecards = computed(() => this.unofficialCount() > 0);

  totalRoundCount = computed(() => {
    const tournament = this.selectedTournament();
    return tournament?.roundsCount || this.rounds().length || 4;
  });

  ngOnInit(): void {
    const tournament = this.selectedTournament();
    const round = this.selectedRound();

    if (!tournament || !round) {
      this.toastr.error('Please select a tournament and round first', 'Error');
      this.router.navigate(['/rounds']);
      return;
    }

    if (tournament.tournamentId) {
      this.loadRounds(tournament.tournamentId);
      this.loadUnofficialScorecards(tournament.tournamentId, round.roundId!);
    }
  }

  loadRounds(tournamentId: string): void {
    this.tournamentService.getRounds(tournamentId).subscribe({
      next: (rounds) => {
        this.rounds.set(rounds);
      },
      error: (error) => {
        this.loggingService.error('RoundCloseComponent', 'Error loading rounds:', error);
      }
    });
  }

  loadUnofficialScorecards(tournamentId: string, roundId: string): void {
    this.tournamentService.getUnofficialScorecards(tournamentId, roundId).subscribe({
      next: (result) => {
        this.unofficialCount.set(result.unofficialScorecardCount);
        this.unofficialScorecards.set(result.unofficialScorecards);
      },
      error: (error) => {
        this.loggingService.error('RoundCloseComponent', 'Error loading unofficial scorecards:', error);
        this.unofficialCount.set(0);
        this.unofficialScorecards.set([]);
      }
    });
  }

  async confirmCloseRound(): Promise<void> {
    const round = this.selectedRound();
    const tournament = this.selectedTournament();

    if (!round || !tournament) {
      this.toastr.error('Please select a tournament and round first', 'Error');
      return;
    }

    if (this.hasUnofficialScorecards()) {
      this.toastr.error(
        `${this.unofficialCount()} scorecards must be marked Official before closing this round.`,
        'Round cannot be closed'
      );
      return;
    }

    const isFinalRound = round.number === this.totalRoundCount();

    const confirmMessage = isFinalRound
      ? `** This is the Final Round of the Tournament. **\n\nClosing the Round will mark the Round as Complete and Official.\nAre you sure you want to proceed?\n\nReminder: After you verify the Tournament Summary,\n   close the Tournament (Tournament > Close Tournament).`
      : `Closing the Round will mark the Round as Complete and Official.\n It will perform any Cuts that need to be made.\n Are you sure you want to proceed?`;

    const confirmed = await this.dialogService.confirm(confirmMessage, {
      title: isFinalRound ? 'Close Final Round' : 'Close Round',
      confirmLabel: isFinalRound ? 'Close Final Round' : 'Close Round',
      cancelLabel: 'Cancel'
    });

    if (!confirmed) {
      return;
    }

    await this.executeCloseRound(false);
  }

  private async executeCloseRound(force: boolean = false): Promise<void> {
    const tournament = this.selectedTournament();
    const round = this.selectedRound();

    if (!tournament?.tournamentId || !round?.roundId) {
      return;
    }

    const tournamentId = tournament.tournamentId!;
    const roundId = round.roundId!;

    this.isClosing.set(true);

    try {
      const result = await firstValueFrom(this.tournamentService.closeRound(tournamentId, roundId, force));
      this.closeResult.set(result);
      this.toastr.success(result?.message || 'Round closed successfully', 'Success');
      this.isClosing.set(false);
      this.refreshRoundsAfterClose(tournamentId);
    } catch (error: any) {
      this.loggingService.error('RoundCloseComponent', 'Error closing round:', error);

      const problem = error?.error;
      const errorMessage = problem?.detail || problem?.message || 'Failed to close round';
      this.toastr.error(errorMessage, 'Error');
      this.isClosing.set(false);
    }
  }

  async markAllUnofficialScorecardsOfficial(): Promise<void> {
    const round = this.selectedRound();
    const tournament = this.selectedTournament();

    if (!round || !tournament) {
      this.toastr.error('Please select a tournament and round first', 'Error');
      return;
    }

    const unofficial = this.unofficialScorecards() || [];

    // Prefer scorecardId if provided by the API
    let scorecardIds: string[] = unofficial
      .map((s: any) => s.scorecardId)
      .filter((id: any) => !!id);

    // If only BusinessEntityId values are present, resolve to scorecardIds by fetching round scorecards
    if (scorecardIds.length === 0 && this.unofficialCount() > 0) {
      const roundScorecards = await firstValueFrom(this.scorecardService.getByRound(round.roundId!));
      const idMap = new Map<number, string>(
        (roundScorecards || [])
          .filter((rc: any) => rc?.businessEntityId && rc?.scorecardId)
          .map((rc: any) => [rc.businessEntityId as number, rc.scorecardId as string])
      );

      const beIds: number[] = unofficial
        .map((s: any) => s.businessEntityId ?? s.businessEntityID)
        .filter((v: any) => typeof v === 'number');

      scorecardIds = beIds
        .map((be) => idMap.get(be))
        .filter((id): id is string => !!id);
    }

    if (scorecardIds.length === 0) {
      if (this.unofficialCount() > 0) {
        this.toastr.error('Cannot resolve scorecard IDs from unofficial list. Please refresh or mark Official via admin.', 'Missing IDs');
      } else {
        this.toastr.info('No unofficial scorecards to mark Official', 'Info');
      }
      return;
    }

    const confirm = await this.dialogService.confirmMarkAllOfficial({
      count: scorecardIds.length,
      roundNumber: round.number ?? 0,
      tournamentName: tournament.name ?? 'Tournament'
    });

    if (!confirm) {
      return;
    }

    this.isMarkingOfficial.set(true);

    try {
      await firstValueFrom(forkJoin(scorecardIds.map((id) => this.scorecardService.markOfficial(id))));
      this.toastr.success(`Marked ${scorecardIds.length} scorecards Official`, 'Success');
      this.loadUnofficialScorecards(tournament.tournamentId!, round.roundId!);
    } catch (error: any) {
      this.loggingService.error('RoundCloseComponent', 'Error marking scorecards official:', error);
      const problem = error?.error;
      const errorMessage = problem?.detail || problem?.message || 'Failed to mark scorecards Official';
      this.toastr.error(errorMessage, 'Error');
    } finally {
      this.isMarkingOfficial.set(false);
    }
  }

  private refreshRoundsAfterClose(tournamentId: string): void {
    this.tournamentService.getRounds(tournamentId).subscribe({
      next: (rounds) => {
        this.rounds.set(rounds);
        this.loggingService.info('RoundCloseComponent', 'Rounds refreshed after close');
        this.router.navigate(['/rounds']);
      },
      error: (error) => {
        this.loggingService.error('RoundCloseComponent', 'Error refreshing rounds after close:', error);
      }
    });
  }
}
